//Material editor's main purpose is to reflect the properties of a single material asset

import React from 'react';
import { useDispatch } from 'react-redux';
import MaterialPropertyTypes from './MaterialPropertyTypes';
import MaterialStructure from './MaterialStructure';
import NumberProperty from '../properties/NumberProperty';
import SliderProperty from '../properties/SliderProperty';
import BoolProperty from '../properties/BoolProperty';
import ColorProperty from '../properties/ColorProperty';
import StringProperty from '../properties/StringProperty';
import DropdownProperty from '../properties/DropdownProperty';
import AssetProperty from '../properties/AssetProperty';
import { StoreActions } from '../../state/store';
import { AssetTypes, CustomEventTypes } from '../../globals';
import AssetsManager from '../../classes/AssetsManager';
import { useTypedDispatch } from '../../state/hooks';

//props should contain the selected material id and property object {type, name, value}

interface MaterialEditorProps {
	"data-name": string;
	id: string;
	key: string;
	name: string;
	property:  {[p: string]: any}
}

interface Option {
	display: string;
	value: string;
}

export default function MaterialEditor(props: MaterialEditorProps)
{
	const dispatch = useTypedDispatch();
	const property = props.property;

	function generateMaterialPresets()
	{
		const options: Option[] = [];
		for (const key of Object.keys(MaterialStructure))
		{
			options.push({ display: key, value: key });
		}

		return options;
	}

	function onValueChange(newValue)
	{
		if(props.name === "name" && !newValue) return;
		dispatch(StoreActions.material.setMaterialProperty({
			id: props.id,
			property: props.name,
			value: newValue,
		}));
		if("name" === props.name)
		{
			document.dispatchEvent(new CustomEvent(CustomEventTypes.updateMaterialComponent));
		}
	}

	function onSubmit(newValue: any = null)
	{
		if (newValue)
		{
			onValueChange(newValue);
		}
		dispatch(StoreActions.material.submitMaterial({
			id: props.id,
			property: props.name
		}));
	}

	if (props.property.visible)
	{
		if (property.type === MaterialPropertyTypes.NUMBER)
		{
			return (
				<NumberProperty
					data-name={props["data-name"] ? props["data-name"] + "-" + props.name : "material-editor"}
					label={props.name}
					value={property.value}
					onChangeCallback={onValueChange}
					onCommitCallback={onSubmit}
					min={property.min ?? 0}
					step={property.step ?? 0.1}
				/>
			);
		}
		else if (property.type === MaterialPropertyTypes.SLIDER)
		{
			return (
				<SliderProperty
					data-name={props["data-name"] ? props["data-name"] + "-" + props.name : "material-editor"}
					label={props.name}
					value={property.value}
					onChangeCallback={onValueChange}
					onCommitCallback={onSubmit}
					min={property.min ?? 0}
					max={property.max ?? 1}
					step={property.step ?? 0.1}
				/>
			);
		}
		else if (property.type === MaterialPropertyTypes.TYPE)
		{
			return (
				<DropdownProperty
					data-name={props["data-name"] ? props["data-name"] + "-"+props.name : "material-editor"}
					label={"Material Preset"}
					value={property.value}
					onChangeCallback={onSubmit}
					options={generateMaterialPresets()}
				/>
			);
		}
		else if (property.type === MaterialPropertyTypes.TOGGLE)
		{
			return (
				<BoolProperty
					data-name={props["data-name"] ? props["data-name"] + "-"+props.name : "material-editor"}
					label={props.name}
					value={property.value}
					onChangeCallback={onSubmit}
				/>
			);
		}
		else if (property.type === MaterialPropertyTypes.COLOR)
		{
			return (
				<ColorProperty
					data-name={props["data-name"] ? props["data-name"] + "-"+props.name : "material-editor"}
					label={props.name}
					value={property.value ? property.value : "#ffffff"}
					onChangeCallback={(v) => onValueChange(v)}
					onCommitCallback={onSubmit}
				/>
			);
		}
		else if (property.type === MaterialPropertyTypes.TEXT)
		{
			return (
				<StringProperty
					data-name={props["data-name"] ? props["data-name"] + "-"+props.name : "material-editor"}
					label={props.name}
					value={property.value}
					onChangeCallback={onValueChange}
					onCommitCallback={onSubmit}
				/>
			);
		}
		else if (property.type === MaterialPropertyTypes.TEXTURE)
		{
			return (
				<AssetProperty
					data-name={props["data-name"] ? props["data-name"] + "-"+props.name : "material-editor"}
					label={props.name}
					type={AssetTypes.Image}
					value={property.value}
					onChangeCallback={(value) =>
					{
						onValueChange(value);

						// material assets are handled in a different manner
						AssetsManager.removeUsedAsset(value);
					}}
				/>
			);
		}
		else if (property.type === MaterialPropertyTypes.VIDEO)
		{
			return (
				<AssetProperty
					data-name={props["data-name"] ? props["data-name"] + "-"+props.name : "material-editor"}
					label={props.name}
					type={AssetTypes.Video}
					value={property.value}
					onChangeCallback={(value) =>
					{
						onValueChange(value);

						// material assets are handled in a different manner
						AssetsManager.removeUsedAsset(value);
					}}
				/>
			);
		}
		else if (property.type === MaterialPropertyTypes.OPTIONS && property.options)
		{
			return (
				<DropdownProperty
					data-name={props["data-name"] ? props["data-name"] + "-"+props.name : "material-editor"}
					label={props.name}
					value={property.value}
					onChangeCallback={onSubmit}
					options={
						property.options.map(option =>
						{
							return ({
								display: option,
								value: option,
							});
						})
					}
				/>
			);
		}
	}

	return null;
}